/**
 * agreeJump.ts — when the q15 agreement step shows its floating "Go to the
 * agreement tick" arrow, measured rather than guessed.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: checkout dedupe + q15
 * (the CTO, 2026-09-26: fewer presses, and a mobile shortcut).
 *
 * WHY. On a phone the q15 declarations are a long read list (the real v6 wording
 * runs to ~1,040 characters), and the one "I agree to all of the above" tick
 * sits under all of it — at 375×812 it lands ~1,120px down the page, a screen
 * and a half below the fold. The journey's sticky bar keeps Continue on screen,
 * but Continue waits for that tick, so the patient has to hunt for it. The arrow
 * is the shortcut: one press scrolls to the tick and puts the keyboard on it.
 *
 * WHEN IT SHOWS — all three, or nothing:
 *   - the viewport is a phone's (`AGREE_JUMP_QUERY`, the journey's 880px
 *     breakpoint); at ≥880px the step is one wide container and the tick is
 *     above a laptop's fold;
 *   - `IntersectionObserver` exists — NO OBSERVER ⇒ NEVER SHOWN. A button that
 *     cannot tell when to go away would sit over the page for good, and it is a
 *     convenience: the tick is always reachable by scrolling;
 *   - the tick's row is BELOW the visible page, where "visible" stops
 *     `AGREE_JUMP_BAR_RESERVE_PX` short of the bottom edge — the sticky bar,
 *     which is 107px at 375×812 while its reason line shows (~128px wrapped),
 *     covers that band, so a tick behind the bar still counts as not on screen.
 * Scrolled PAST the tick (it is above the viewport), the arrow hides too: it
 * only ever points down, and the patient has already seen the tick.
 *
 * ⚠️ THE OBSERVER ALONE GOES STALE ON A JUMP (review, 2026-09-26, reproduced in
 * Chrome at 375×812). An IntersectionObserver reports only when the row's
 * INTERSECTING state changes, and a scroll that carries the row from one side of
 * the visible band to the other in a single frame never intersects — End/Home,
 * Ctrl/Cmd+arrow, find-in-page, a focus jump, any instant scroll under reduced
 * motion. From the top, End left the arrow up and pointing down while the tick
 * sat ABOVE the screen; Home back left it missing while the tick was below the
 * fold again. A finger scroll passes through the band, which is why a touch test
 * never saw it. So the answer is also re-read from the row's own rectangle on
 * scroll and resize — passive listeners, at most once a frame — with the same
 * rule the observer's entries give: below ⇔ the row's top is at or past the
 * bottom of the visible band. The observer stays: it is still what answers a
 * layout change with no scroll, and still the gate — no observer, no arrow.
 */
import { useEffect, useState } from 'react';

/** The phone layout — the journey's own breakpoint (tokens.css, below 880px). */
export const AGREE_JUMP_QUERY = '(max-width: 879.98px)';

/**
 * How far above the bottom edge the tick must be to count as "on screen": 9rem,
 * the SAME figure q15 pads its focus scrolling by while its reason line shows
 * (tokens.css, "journey-ux: consent step") — the height of the phone bar with
 * room for the focus ring. The arrow itself sits at this height too, so it is
 * always clear of the bar's button.
 */
export const AGREE_JUMP_BAR_RESERVE_PX = 144;

function phoneMatch(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(AGREE_JUMP_QUERY);
}

/**
 * The one rule, from a rectangle: the row is below the visible page when its top
 * is at or past the bottom of the visible band (the viewport less the bar's
 * reserve). The observer's entries give the same answer; this is the scroll
 * listener's form of it.
 */
export function isBelowTheFold(rowTop: number, viewportHeight: number): boolean {
  return rowTop >= viewportHeight - AGREE_JUMP_BAR_RESERVE_PX;
}

/**
 * True while `node` (the tick's row) is below the visible part of a phone's
 * page. False on a wider viewport, with no `IntersectionObserver` or no
 * `matchMedia`, and before the node exists.
 */
export function useBelowTheFold(node: Element | null): boolean {
  const [phone, setPhone] = useState(() => phoneMatch()?.matches === true);
  const [below, setBelow] = useState(false);

  useEffect(() => {
    const mql = phoneMatch();
    if (!mql) return;
    const onChange = () => setPhone(mql.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (!node || !phone || typeof IntersectionObserver === 'undefined') {
      setBelow(false);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        const visibleBottom =
          entry.rootBounds?.bottom ?? (typeof window !== 'undefined' ? window.innerHeight - AGREE_JUMP_BAR_RESERVE_PX : 0);
        setBelow(!entry.isIntersecting && entry.boundingClientRect.top >= visibleBottom);
      },
      { rootMargin: `0px 0px -${AGREE_JUMP_BAR_RESERVE_PX}px 0px` },
    );
    io.observe(node);

    /* The jump-proof re-read (see the ⚠️ above), throttled to one per frame. */
    let frame = 0;
    const remeasure = () => {
      frame = 0;
      setBelow(isBelowTheFold(node.getBoundingClientRect().top, window.innerHeight));
    };
    const schedule = () => {
      if (frame) return;
      if (typeof requestAnimationFrame === 'function') frame = requestAnimationFrame(remeasure);
      else remeasure();
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    return () => {
      io.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
    };
  }, [node, phone]);

  return phone && below;
}
